import {ChangeDetectionStrategy, Component, computed, ElementRef, input, output, signal, viewChild} from "@angular/core"
import {Box2Like, Size2Like, Vector2Like} from "@cm/math"
import {
    applyReferenceRegionDrag,
    ReferenceRegionCorner,
    ReferenceRegionDrag,
} from "@app/textures/texture-editor/operator-stack/operators/recolor/helpers/reference-region-geometry"

const MAX_PREVIEW_HEIGHT_PX = 240

// on screen, so that a plain click doesn't count as a drag
const MIN_DRAG_DISTANCE_PX = 3

// Shows a reference image's preview and lets the user draw a rectangle on it. All regions are in
// full-resolution pixels of the reference image, independent of the size of the preview.
@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-reference-region-selector",
    templateUrl: "./reference-region-selector.component.html",
    styleUrl: "./reference-region-selector.component.scss",
})
export class ReferenceRegionSelectorComponent {
    readonly $previewUrl = input.required<string>({alias: "previewUrl"})
    readonly $imageSize = input.required<Size2Like>({alias: "imageSize"})
    readonly $region = input<Box2Like | undefined>(undefined, {alias: "region"})
    readonly $disabled = input(false, {alias: "disabled"})
    readonly regionChange = output<Box2Like>()

    private readonly $surface = viewChild.required<ElementRef<HTMLElement>>("surface")

    // what is being dragged, only set during a drag. Afterwards $region is the only truth, so the owner
    // has to update it right away for the rectangle to stay where it was dropped.
    private readonly $draftRegion = signal<Box2Like | undefined>(undefined)
    private activeDrag: {pointerId: number; drag: ReferenceRegionDrag; clientStart: Vector2Like; moved: boolean} | undefined

    protected readonly corners: ReferenceRegionCorner[] = ["nw", "ne", "sw", "se"]

    protected readonly $surfaceWidth = computed(() => {
        const {width, height} = this.$imageSize()
        return `min(100%, ${(MAX_PREVIEW_HEIGHT_PX * width) / height}px)`
    })

    protected readonly $surfaceAspectRatio = computed(() => {
        const {width, height} = this.$imageSize()
        return `${width} / ${height}`
    })

    protected readonly $regionBox = computed(() => {
        const region = this.$draftRegion() ?? this.$region()
        if (!region) {
            return undefined
        }
        const {width, height} = this.$imageSize()
        return {
            left: `${(region.x / width) * 100}%`,
            top: `${(region.y / height) * 100}%`,
            width: `${(region.width / width) * 100}%`,
            height: `${(region.height / height) * 100}%`,
        }
    })

    protected onPointerDown(event: PointerEvent): void {
        if (this.$disabled() || event.button !== 0) {
            return
        }
        event.preventDefault()
        this.$surface().nativeElement.setPointerCapture(event.pointerId)
        this.activeDrag = {
            pointerId: event.pointerId,
            drag: this.dragFromTarget(event),
            clientStart: {x: event.clientX, y: event.clientY},
            moved: false,
        }
    }

    private dragFromTarget(event: PointerEvent): ReferenceRegionDrag {
        const region = this.$region()
        const target = event.target as HTMLElement
        const corner = target.dataset["corner"] as ReferenceRegionCorner | undefined
        if (region && corner) {
            return {type: "resize", corner, region}
        }
        const start = this.toImagePoint(event)
        return region && target.classList.contains("cm-region") ? {type: "move", start, region} : {type: "create", start}
    }

    protected onPointerMove(event: PointerEvent): void {
        const activeDrag = this.activeDrag
        if (activeDrag?.pointerId !== event.pointerId) {
            return
        }
        const clientDistance = Math.hypot(event.clientX - activeDrag.clientStart.x, event.clientY - activeDrag.clientStart.y)
        activeDrag.moved ||= clientDistance >= MIN_DRAG_DISTANCE_PX
        if (activeDrag.moved) {
            this.$draftRegion.set(applyReferenceRegionDrag(activeDrag.drag, this.toImagePoint(event), this.$imageSize()))
        }
    }

    protected onPointerUp(event: PointerEvent): void {
        const activeDrag = this.activeDrag
        if (activeDrag?.pointerId !== event.pointerId) {
            return
        }
        this.activeDrag = undefined
        const draftRegion = this.$draftRegion()
        this.$draftRegion.set(undefined)
        if (activeDrag.moved && draftRegion) {
            this.regionChange.emit(draftRegion)
        }
    }

    protected onPointerCancel(event: PointerEvent): void {
        if (this.activeDrag?.pointerId === event.pointerId) {
            this.activeDrag = undefined
            this.$draftRegion.set(undefined)
        }
    }

    private toImagePoint(event: PointerEvent): Vector2Like {
        const bounds = this.$surface().nativeElement.getBoundingClientRect()
        const {width, height} = this.$imageSize()
        return {
            x: ((event.clientX - bounds.left) / bounds.width) * width,
            y: ((event.clientY - bounds.top) / bounds.height) * height,
        }
    }
}
